import { useCallback, useEffect, useState, useRef, SetStateAction, Dispatch } from 'react'
import { Container, Slider, SliderTrack, SliderRange, SliderValue, Thumb } from './SingleRangeSlider.styles'

interface SingleRangeSliderProps {
  min: number
  max: number
  // Used to persist value of thumb
  value?: number
  setValue: Dispatch<SetStateAction<number>>
  onChange: (value: number) => void
  onMouseUp: () => void
  onTouchEnd: () => void
}

const SingleRangeSlider: React.FC<SingleRangeSliderProps> = ({ min, max, onChange, value, setValue, onMouseUp, onTouchEnd }) => {
  const [val, setVal] = useState<number>(value !== undefined ? value : min)
  const valRef = useRef<HTMLInputElement>(null)
  const prevVal = useRef<number | null>(null)
  const range = useRef<HTMLDivElement>(null)

  const getPercent = useCallback((value: number) => Math.round(((value - min) / (max - min)) * 100), [min, max])

  useEffect(() => {
    setValue(val)
  }, [val, setValue])

  useEffect(() => {
    if (value !== undefined && value !== val) {
      setVal(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  useEffect(() => {
    if (valRef.current) {
      const percent = getPercent(+valRef.current.value)

      if (range.current) {
        range.current.style.width = `${percent}%`
      }
    }
  }, [val, getPercent])

  useEffect(() => {
    if (prevVal.current !== val) {
      onChange(val)
      prevVal.current = val
    }
  }, [onChange, val])

  return (
    <Container>
      <Thumb
        data-testid="slider-thumb"
        min={min}
        max={max}
        value={val}
        ref={valRef}
        onChange={(event) => {
          const value = Math.max(Math.min(+event.target.value, max), min)
          setVal(value)
          event.target.value = value.toString()
        }}
        onMouseUp={onMouseUp}
        onTouchEnd={onTouchEnd}
      />

      <Slider>
        <SliderTrack data-testid="slider-track" />
        <SliderRange data-testid="slider-range" ref={range} />
        <SliderValue data-testid="value" className="slider-value">
          {val.toLocaleString()}
        </SliderValue>
      </Slider>
    </Container>
  )
}

export default SingleRangeSlider
